/**
 * The clock that stamps `updated_at` on everything this phone changes.
 *
 * `Date.now()` is not good enough for it, and the reason is subtle enough to
 * be worth stating plainly.
 *
 * Conflict resolution is last-write-wins per row, and equal timestamps are
 * broken by `updated_by_device` (`newerThanStored()` in `api/src/sync/
 * apply.ts`). That rule was written for two phones and it is right for two
 * phones. It has nothing to say about **one** phone: two edits from the same
 * device with the same millisecond compare equal on the timestamp *and* equal
 * on the device, so the second one is discarded — by the local apply on the
 * way in, and by the server on the way out.
 *
 * Which sounds theoretical until somebody taps **+** twice. Two taps a
 * millisecond apart is not a race a person has to try to cause; it is the
 * ordinary speed of a thumb, and the symptom is a quantity that goes to two
 * and stays there. Worse, the local mirror and the server agree about it, so
 * nothing anywhere reports a problem.
 *
 * Two edits by one person are inherently ordered — they happened in an order,
 * and the person watched them happen. Giving them equal timestamps throws that
 * ordering away. So this clock never returns the same value twice: it follows
 * the wall clock, and when the wall clock has not moved it advances by a
 * millisecond of its own.
 *
 * The drift that can introduce is bounded by how fast someone can tap, so it
 * is milliseconds — far inside the tolerance of a rule that exists to order
 * edits made minutes and hours apart on two different phones.
 */

let last = 0;

/** An ISO 8601 instant that is always strictly later than the previous one. */
export function stamp(): string {
  const now = Date.now();
  last = now > last ? now : last + 1;
  return new Date(last).toISOString();
}

/** Test seam. Nothing in the app resets the clock. */
export function resetClock(): void {
  last = 0;
}
